import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { pdfjs } from "react-pdf";

import { Box, Paper, useTheme } from "@mui/material";
import {
  FormPolygon,
  IProcessedTag,
  Nullable,
  Polygons,
  Size,
  TaggerDocument,
  TagPhrasePayload,
  Word,
  WordPolygon
} from "store/types";
import { defineNumberFormat, separatedStringToNumber } from "utils/strings";

import { useModals } from "../../../../hooks";
import { useTaggerActions } from "../../../../store";
import { AltiusErrors } from "../../../../store/types";
import { IEditTagPayload, IFileData } from "../../../../store/types/tagger/api";
import Rectangle from "../rectangle";
import useDraggableContent from "../useDraggableContent";
import DocumentWrapper from "./DocumentWrapper";
import PDFTopBar from "./PDFTopBar";
import { TagMenu, TagMenuProps } from "./TagMenu";

import "./PDFViewer.styles.scss";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export interface PDFFileViewProps {
  docId: TaggerDocument["id"];
  file: File;
  error: string | null;
  polygons: Nullable<Polygons>;
  tags: IProcessedTag[];
  onCreateTag: (data: Omit<TagPhrasePayload, "deal_id" | "document_id" | "is_measure_global_value">) => void;
  setTableOpen: () => void;
  documentFileData: IFileData;
}

const PDFViewer: React.FC<PDFFileViewProps> = React.memo(
  ({ docId, file, error, polygons, tags, onCreateTag, setTableOpen, documentFileData }) => {
    const [numPages, setNumPages] = useState<number>(NaN);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<Size>();
    const [originalScale, setOriginalScale] = useState<number>(1);
    const [menuOpen, setMenuOpen] = useState<"measure" | "recommendation" | undefined>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedWord, setSelectedWord] = useState<Word | null>(null);
    const [userScale, setUserScale] = useState(1);

    const { editTag } = useTaggerActions();
    const { pdfTaggerErrorModal } = useModals();

    const selectedPolygonsTag = useMemo(
      () =>
        selectedWord ? tags.find((tag) => tag.words.some((word) => word.rectId === selectedWord.rectId)) : undefined,
      [tags, selectedWord]
    );

    const handleEditTag = useCallback(
      async (
        existingTag: IProcessedTag,
        data: Omit<
          IEditTagPayload,
          "id" | "split_id" | "description" | "accounting_date" | "children" | "document_series_id"
        >
      ) => {
        try {
          await editTag({
            id: existingTag.id,
            split_id: existingTag.split_id,
            description: existingTag.description,
            accounting_date: existingTag.accounting_date,
            children: existingTag.children,
            ...data,
            ...(existingTag.series_id && { document_series_id: existingTag.series_id })
          });
        } catch (e) {
          const error = e as AltiusErrors;
          // If error has validation errors, throw it so TagMenu can handle them
          if (error?.errors && Object.keys(error.errors).length > 0) {
            throw e;
          }
          // Otherwise, show the modal for non-validation errors
          pdfTaggerErrorModal(error.errors!);
        }
      },
      [editTag, pdfTaggerErrorModal]
    );

    // const isSelectedTagSplitable = useMemo<boolean>(() => {
    //   if (!selectedWord) return false;

    //   return Boolean(tags.find((tag) => tag.words.find((word) => word.rectId === selectedWord.rectId))?.splittable);
    // }, [tags, selectedWord]);

    const theme = useTheme();

    useDraggableContent(docId.toString());

    useEffect(() => {
      setPageNumber(1);
    }, [file]);

    const onDocumentLoadSuccess = useCallback(
      ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
      },
      [setNumPages]
    );

    // const wordSelected = (rectId: string) => {
    //   return selectedWords.some((rect) => rect.rectId === rectId);
    // };

    const closeMenu = () => {
      setAnchorEl(null);
      setMenuOpen(undefined);
      setSelectedWord(null);
    };

    const handleContextMenu = useCallback(
      (e: MouseEvent<HTMLDivElement>, rectId: string, text: string) => {
        e.preventDefault();
        // Define format, clear excess symbols and set selected word
        const tagValue = tags.find((tag) => tag.words.some((word) => word.rectId === rectId))?.value;
        if (tagValue) {
          // if tag exist, set the in the <TagMenu/> real value of the selected polygon
          text = tagValue.toLocaleString();
        }

        setSelectedWord({
          rectId,
          text: String(separatedStringToNumber(text, defineNumberFormat(text)))
        });
        setAnchorEl(e.currentTarget);
        setMenuOpen("measure");
      },
      [tags]
    );

    const handleSelectTag: TagMenuProps["onSelect"] = async ({
      measure_id,
      is_draft,
      split_by,
      date,
      investor_id,
      round_id,
      currency,
      investor_ids
    }) => {
      if (selectedWord) {
        const tagData = {
          measure_id,
          file_polygon_id: selectedWord.rectId,
          value: separatedStringToNumber(selectedWord.text, defineNumberFormat(selectedWord.text)),
          is_draft,
          split_by,
          date,
          investor_id,
          round_id,
          currency,
          investor_ids
        };

        try {
          if (selectedPolygonsTag && !selectedPolygonsTag.deleted_at) {
            await handleEditTag(selectedPolygonsTag, {
              ...tagData,
              investor_id: tagData.investor_id,
              round_id: tagData.round_id
            });
          } else {
            await onCreateTag(tagData);
          }
          // Only close menu and open table on success
          closeMenu();
          setTableOpen();
        } catch (err) {
          // Re-throw the error so TagMenu can handle validation errors
          // Don't close the menu so errors can be displayed
          throw err;
        }
      }
    };

    const [recs, childRecs, formRecs] = useMemo(() => {
      const recs: Record<string, any> = {};
      const childRecs: Set<string> = new Set<string>();
      const formRecs: any[] = [];
      if (polygons?.[pageNumber] && pageSize) {
        polygons[pageNumber].forEach((poly) => {
          const css = {
            left: poly.boundingBox.Left * pageSize.width * originalScale * userScale,
            top: poly.boundingBox.Top * pageSize.height * originalScale * userScale,
            width: poly.boundingBox.Width * pageSize.width * originalScale * userScale,
            height: poly.boundingBox.Height * pageSize.height * originalScale * userScale
          };

          if (poly.hasOwnProperty("text")) {
            recs[poly.id] = {
              text: (poly as WordPolygon).text,
              id: poly.id,
              css: css
            };
          } else {
            formRecs.push({
              children: (poly as FormPolygon).children,
              id: poly.id,
              css: css
            });
            (poly as FormPolygon).children.forEach((id) => childRecs.add(id));
          }
        });
      }

      return [recs, childRecs, formRecs];
    }, [polygons, pageNumber, originalScale, userScale]);

    const taggedRectangles = tags.reduce<Record<string, string>>((acc, tag) => {
      tag.words.forEach((word) => {
        if (!tag.deleted_at) {
          // Don't show deleted tags
          if (!acc[word.rectId] || !tag.is_analysis) {
            // Tags override Suspects
            acc[word.rectId] = tag.measure.name;
          }
        }
      });
      return acc;
    }, {});

    const filememo = useMemo(() => file, [file?.name]);

    const handleValueChange = (newValue: number) => {
      if (selectedWord) {
        setSelectedWord({
          ...selectedWord,
          text: newValue.toLocaleString()
        });
      }
    };

    const tagMenuValue = useMemo(() => {
      return separatedStringToNumber(selectedWord?.text || "", defineNumberFormat(selectedWord?.text || ""));
    }, [selectedWord]);

    const tagMenuNumberFormat = useMemo(() => {
      return defineNumberFormat(selectedWord?.text || "");
    }, [selectedWord]);

    return (
      <Paper className="document-container">
        <PDFTopBar
          zoom={userScale}
          setZoom={setUserScale}
          loading={!polygons}
          error={error}
          filename={file.name}
          numPages={numPages}
          setPageNumber={(value) => {
            setPageNumber(value);
          }}
          pageNumber={pageNumber}
        />
        <div
          className="page-container"
          style={{
            width: pageSize ? Math.floor(pageSize.width * originalScale) : undefined,
            height: pageSize ? Math.floor(pageSize.height * originalScale) : undefined,
            overflow: "hidden",
            backgroundColor: theme.palette.primary.main
          }}
          // onContextMenu={handleContextMenu}
          // onMouseUp={handleMouseUp}
        >
          <div id={docId.toString()} className="tagger__draggable">
            <DocumentWrapper
              file={filememo}
              pageNumber={pageNumber}
              originalScale={originalScale}
              userScale={userScale}
              setPageSize={setPageSize}
              setOriginalScale={setOriginalScale}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
            />
            {Object.keys(recs).map((rectId) => {
              if (!childRecs.has(rectId) && recs[rectId]) {
                return (
                  <Rectangle
                    key={rectId}
                    className="form-word"
                    rectangle={recs[rectId]}
                    taggedRectangles={taggedRectangles}
                    tags={tags}
                    selectedWord={selectedWord}
                    onClick={handleContextMenu}
                    // onEnter={handleWordEnter}
                  />
                );
              }
              return null;
            })}
            {formRecs.map((formRec) => {
              const currentRectangleData = tags.find((tag) =>
                tag.words.some((word) => formRec.children.includes(word.rectId))
              );
              const isWordSelected = formRec.children.includes(selectedWord?.rectId);
              return (
                <Box
                  key={formRec.id}
                  sx={{
                    position: "absolute",
                    padding: "1px",
                    transform: "translate(-2px, -2px)",
                    boxSizing: "content-box",
                    border: "1px solid gold",
                    zIndex: "1",
                    ...(currentRectangleData && {
                      background: "#07a27d33"
                    }),
                    ...(isWordSelected && {
                      background: "rgba(0, 0, 0, 0.10)",
                      opacity: 0.5
                    }),
                    ...formRec.css
                  }}
                >
                  {formRec.children.map((recId: string) =>
                    recs[recId] ? (
                      <Rectangle
                        key={recId}
                        taggedRectangles={taggedRectangles}
                        rectangle={recs[recId]}
                        anchorCSS={formRec.css}
                        tags={tags}
                        selectedWord={selectedWord}
                        // onEnter={handleWordEnter}
                        onClick={handleContextMenu}
                      />
                    ) : null
                  )}
                </Box>
              );
            })}
          </div>
        </div>
        <TagMenu
          docId={docId}
          open={menuOpen === "measure"}
          onClose={closeMenu}
          onSelect={handleSelectTag}
          anchorEl={anchorEl}
          selectedPolygonsTag={selectedPolygonsTag}
          selectedValue={tagMenuValue}
          onValueChange={handleValueChange}
          numberFormat={tagMenuNumberFormat}
          documentFileData={documentFileData}
        />
      </Paper>
    );
  }
);

export default PDFViewer;
